import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const TOOLTIP_ID = "global-title-tooltip";
const VIEWPORT_MARGIN = 12;
const TARGET_GAP = 6;
const MOBILE_TOOLTIP_BREAKPOINT = 768;

interface TooltipContent { element: Element; text: string; }
interface ActiveTooltip extends TooltipContent {
  hovered: boolean;
  focused: boolean;
  dismissed?: boolean;
}
interface TooltipPosition { left: number; top: number; }
type TooltipSource = "hovered" | "focused";
type TooltipMouseEvent = MouseEvent & { sourceCapabilities?: { firesTouchEvents?: boolean } | null };

const supportsHoverTooltip = () => {
  // Chrome device emulation and hybrid laptops can report a coarse primary
  // pointer even while a real mouse is producing hover events. In that case
  // relying on matchMedia leaves the native browser `title` visible. The
  // actual pointer type is filtered below; viewport width only decides
  // whether hover tooltips fit comfortably on screen.
  return typeof window !== "undefined" && window.innerWidth > MOBILE_TOOLTIP_BREAKPOINT;
};

const asElement = (node: EventTarget | null | undefined): Element | null => (
  node instanceof Element ? node : node instanceof Node ? node.parentElement : null
);

const findTitledElement = (node: EventTarget | null, activeElement: Element | null | undefined) => {
  const element = asElement(node);
  if (!element) return null;
  if (activeElement?.contains(element)) return activeElement;
  return element.closest?.("[title]") || null;
};

const addTooltipDescription = (element: Element) => {
  const ids = new Set((element.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean));
  ids.add(TOOLTIP_ID);
  element.setAttribute("aria-describedby", [...ids].join(" "));
};

const removeTooltipDescription = (element: Element) => {
  const ids = (element.getAttribute("aria-describedby") || "")
    .split(/\s+/)
    .filter((id: string) => id && id !== TOOLTIP_ID);
  if (ids.length) element.setAttribute("aria-describedby", ids.join(" "));
  else element.removeAttribute("aria-describedby");
};

function GlobalTitleTooltip() {
  const activeRef = useRef<ActiveTooltip | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const lastInputWasKeyboardRef = useRef(false);
  const lastPointerTypeRef = useRef("");
  const [tooltip, setTooltip] = useState<TooltipContent | null>(null);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const hideTooltipUntilInteractionEnds = useCallback(() => {
    const active = activeRef.current;
    if (!active) return;

    active.dismissed = true;
    active.focused = false;
    if (active.element.isConnected) removeTooltipDescription(active.element);
    setTooltip(null);
    setPosition(null);
  }, []);

  const closeTooltip = useCallback((updateState: boolean = true) => {
    const active = activeRef.current;
    if (!active) return;

    if (active.element.isConnected) {
      if (!active.element.hasAttribute("title")) {
        active.element.setAttribute("title", active.text);
      }
      removeTooltipDescription(active.element);
    }

    activeRef.current = null;
    if (updateState) {
      setTooltip(null);
      setPosition(null);
    }
  }, []);

  const openTooltip = useCallback((element: Element | null, source: TooltipSource) => {
    if (!element) return;

    const current = activeRef.current;
    if (current?.element === element) {
      if (current.dismissed) return;
      current[source] = true;
      if (element.hasAttribute("title")) element.removeAttribute("title");
      return;
    }

    closeTooltip();

    const text = (element.getAttribute("title") || "").trim();
    if (!text) return;

    const active = {
      element,
      text,
      hovered: source === "hovered",
      focused: source === "focused"
    };
    activeRef.current = active;
    element.removeAttribute("title");
    addTooltipDescription(element);
    setPosition(null);
    setTooltip({ element, text });
  }, [closeTooltip]);

  useEffect(() => {
    const reopenFocusedTooltip = () => {
      if (!lastInputWasKeyboardRef.current) return;
      const focused = findTitledElement(document.activeElement, null);
      if (focused) openTooltip(focused, "focused");
    };

    const handleMouseOver = (event: TooltipMouseEvent) => {
      // Touch browsers can emit a synthetic mouseover after a tap. Opening a
      // hover tooltip from it leaves the top-panel action labelled on every
      // route even though no pointer is hovering the button.
      const isTouchGenerated =
        event.sourceCapabilities?.firesTouchEvents === true ||
        lastPointerTypeRef.current === "touch" ||
        lastPointerTypeRef.current === "pen";
      if (!supportsHoverTooltip() || isTouchGenerated) {
        if (activeRef.current) hideTooltipUntilInteractionEnds();
        return;
      }
      const target = findTitledElement(event.target, activeRef.current?.element);
      if (target) openTooltip(target, "hovered");
    };

    const handlePointerOver = (event: { pointerType: string; }) => {
      if (event.pointerType) lastPointerTypeRef.current = event.pointerType;
    };

    const handleMouseOut = (event: MouseEvent) => {
      const active = activeRef.current;
      if (!active || !active.element.contains(asElement(event.target))) return;
      if (event.relatedTarget && active.element.contains(asElement(event.relatedTarget))) return;

      active.hovered = false;
      if (!active.focused) {
        const wasDismissed = active.dismissed;
        closeTooltip();
        if (!wasDismissed) reopenFocusedTooltip();
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!lastInputWasKeyboardRef.current) return;
      const target = findTitledElement(event.target, activeRef.current?.element);
      if (target) openTooltip(target, "focused");
    };

    const handleFocusOut = (event: FocusEvent) => {
      const active = activeRef.current;
      if (!active || !active.element.contains(asElement(event.target))) return;
      if (event.relatedTarget && active.element.contains(asElement(event.relatedTarget))) return;

      active.focused = false;
      if (!active.hovered) closeTooltip();
    };

    const handlePointerDown = (event: PointerEvent) => {
      lastInputWasKeyboardRef.current = false;
      if (event.pointerType) lastPointerTypeRef.current = event.pointerType;
      const active = activeRef.current;
      if (!active) return;
      if (active.element.contains(asElement(event.target))) {
        hideTooltipUntilInteractionEnds();
      } else {
        closeTooltip();
      }
    };

    const handleClick = (event: MouseEvent) => {
      const active = activeRef.current;
      if (active?.element.contains(asElement(event.target))) {
        hideTooltipUntilInteractionEnds();
      }
    };

    const handleKeyDown = (event: { key: string; }) => {
      lastInputWasKeyboardRef.current = true;
      if (event.key === "Escape") closeTooltip();
    };

    const handleViewportChange = () => {
      if (!supportsHoverTooltip() && activeRef.current) {
        hideTooltipUntilInteractionEnds();
      }
    };

    const handleBlur = () => closeTooltip();

    document.addEventListener("pointerover", handlePointerOver, true);
    document.addEventListener("mouseover", handleMouseOver, true);
    document.addEventListener("mouseout", handleMouseOut, true);
    document.addEventListener("focusin", handleFocusIn, true);
    document.addEventListener("focusout", handleFocusOut, true);
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("resize", handleViewportChange);

    return () => {
      document.removeEventListener("pointerover", handlePointerOver, true);
      document.removeEventListener("mouseover", handleMouseOver, true);
      document.removeEventListener("mouseout", handleMouseOut, true);
      document.removeEventListener("focusin", handleFocusIn, true);
      document.removeEventListener("focusout", handleFocusOut, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("resize", handleViewportChange);
      closeTooltip(false);
    };
  }, [closeTooltip, hideTooltipUntilInteractionEnds, openTooltip]);

  useLayoutEffect(() => {
    if (!tooltip || !tooltipRef.current) return undefined;

    const updatePosition = () => {
      if (!tooltip.element.isConnected || !tooltipRef.current) {
        closeTooltip();
        return;
      }

      const targetRect = tooltip.element.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const appSurface = tooltip.element.closest(".App") || document.querySelector(".App");
      const appRect = appSurface?.getBoundingClientRect?.();
      const boundaryLeft = Math.max(
        VIEWPORT_MARGIN,
        (appRect?.left ?? 0) + VIEWPORT_MARGIN
      );
      const boundaryRight = Math.min(
        window.innerWidth - VIEWPORT_MARGIN,
        (appRect?.right ?? window.innerWidth) - VIEWPORT_MARGIN
      );
      const maxLeft = Math.max(boundaryLeft, boundaryRight - tooltipRect.width);
      const centeredLeft = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
      const left = Math.min(Math.max(centeredLeft, boundaryLeft), maxLeft);
      const above = targetRect.top - tooltipRect.height - TARGET_GAP;
      const below = targetRect.bottom + TARGET_GAP;
      const maxTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - tooltipRect.height - VIEWPORT_MARGIN);
      const top = above >= VIEWPORT_MARGIN
        ? above
        : Math.min(Math.max(below, VIEWPORT_MARGIN), maxTop);

      setPosition({ left, top });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    // Do not restore the native `title` while the pointer is still over the
    // element: after a scroll the browser would immediately show its default
    // black tooltip. Mouseout/focusout restores the attribute normally.
    window.addEventListener("scroll", hideTooltipUntilInteractionEnds, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", hideTooltipUntilInteractionEnds, true);
    };
  }, [closeTooltip, hideTooltipUntilInteractionEnds, tooltip]);

  if (!tooltip || typeof document === "undefined") return null;

  return createPortal(
    <div
      id={TOOLTIP_ID}
      ref={tooltipRef}
      className="global-title-tooltip"
      role="tooltip"
      style={position ? { left: position.left, top: position.top } : undefined}
    >
      {tooltip.text}
    </div>,
    document.body
  );
}

export default GlobalTitleTooltip;
